/**
 * Portada de la sección Onboarding — tarjetas grandes (una por
 * capítulo, más Documentos / Vídeos / FAQ). Es la primera pantalla
 * que ve el auditor cuando pincha "Onboarding" en el menú.
 */

import { Card, Col, Row, Typography, Input, Space, Tag } from "antd";
import {
  FileTextOutlined,
  VideoCameraOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router";
import { useMemo, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { usePageTitle } from "@/hooks";
import {
  chapters,
  documents,
  processes,
  faqs,
} from "@/data/onboarding";
import { chapterMarkdown } from "@/data/onboarding/chapterContent";
import type { Chapter } from "@/data/onboarding/types";
import { getIcon } from "./icons";

const { Title, Text, Paragraph } = Typography;

/** Filtro simple case-insensitive sobre título/summary/label. */
function match(haystack: string | undefined, needle: string): boolean {
  if (!haystack) return false;
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

/** Busca el término en un capítulo (label + title + summary + MD) y
 *  recursivamente en sus sub-capítulos. Un capítulo padre coincide
 *  si él mismo o cualquiera de sus hijos coincide — así "precio"
 *  encuentra "Control" porque el término aparece en cualquiera de sus
 *  sub-capítulos, y encuentra "Quién es quién" porque aparece en el
 *  MD de la cláusula 15 del contrato. */
function chapterMatches(chapter: Chapter, q: string): boolean {
  if (match(chapter.label, q)) return true;
  if (match(chapter.title, q)) return true;
  if (match(chapter.summary, q)) return true;
  const md = chapterMarkdown[chapter.contentSlug];
  if (match(md, q)) return true;
  if (chapter.subChapters?.some((sc) => chapterMatches(sc, q))) return true;
  return false;
}

export const OnboardingHome: React.FC = () => {
  usePageTitle("Onboarding");
  const [query, setQuery] = useState("");

  const filteredChapters = useMemo(() => {
    if (!query.trim()) return chapters;
    const q = query.trim();
    return chapters.filter((c) => chapterMatches(c, q));
  }, [query]);

  // Búsqueda cruzada — pinta contadores de resultados en otras
  // secciones para que el auditor sepa a dónde ir.
  const crossCounts = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.trim();
    return {
      procesos: processes.filter(
        (p) => match(p.title, q) || match(p.purpose, q),
      ).length,
      documentos: documents.filter(
        (d) => match(d.title, q) || match(d.description, q),
      ).length,
      faq: faqs.filter((f) => match(f.question, q) || match(f.answer, q)).length,
    };
  }, [query]);

  return (
    <>
      <PageHeader
        title="Onboarding — Manual del puesto Tang Gold"
        subtitle="Guía para aprender el puesto de Control y Certificación Tang Gold. Empieza por los capítulos, o salta directamente a documentos, vídeos o preguntas frecuentes."
      />

      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Buscar en el manual (capítulos, procesos, documentos, FAQ)…"
        size="large"
        style={{ marginBottom: 20, maxWidth: 640 }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {crossCounts ? (
        <Space size={[6, 6]} wrap style={{ marginBottom: 16 }}>
          <Tag color="blue">{filteredChapters.length} capítulos</Tag>
          <Link to="/onboarding/documentos">
            <Tag color="magenta">{crossCounts.documentos} documentos</Tag>
          </Link>
          <Link to="/onboarding/faq">
            <Tag color="gold">{crossCounts.faq} en FAQ</Tag>
          </Link>
        </Space>
      ) : null}

      <Title level={4} style={{ marginTop: 8 }}>
        Capítulos
      </Title>
      <Row gutter={[16, 16]}>
        {filteredChapters.map((chapter) => (
          <Col key={chapter.slug} xs={24} sm={12} lg={8}>
            <Link to={`/onboarding/capitulos/${chapter.slug}`}>
              <Card
                hoverable
                style={{ height: "100%" }}
                styles={{ body: { display: "flex", gap: 12 } }}
              >
                <div style={{ fontSize: 32, color: "#1677ff", lineHeight: 1 }}>
                  {getIcon(chapter.icon)}
                </div>
                <div style={{ flex: 1 }}>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    {chapter.title}
                  </Title>
                  <Paragraph
                    type="secondary"
                    style={{ marginBottom: 0, fontSize: 13 }}
                  >
                    {chapter.summary}
                  </Paragraph>
                </div>
              </Card>
            </Link>
          </Col>
        ))}
      </Row>

      <Title level={4} style={{ marginTop: 32 }}>
        Recursos
      </Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Link to="/onboarding/documentos">
            <Card hoverable style={{ height: "100%" }}>
              <Space align="start">
                <FileTextOutlined style={{ fontSize: 28, color: "#cf1322" }} />
                <div>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    Documentos ({documents.length})
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Biblioteca de PDFs descargables — protocolos, contratos,
                    presupuestos.
                  </Text>
                </div>
              </Space>
            </Card>
          </Link>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Link to="/onboarding/videos">
            <Card hoverable style={{ height: "100%" }}>
              <Space align="start">
                <VideoCameraOutlined style={{ fontSize: 28, color: "#722ed1" }} />
                <div>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    Vídeo-tutoriales
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Cómo se procesan las muestras de hoja y de cáscara.
                  </Text>
                </div>
              </Space>
            </Card>
          </Link>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Link to="/onboarding/faq">
            <Card hoverable style={{ height: "100%" }}>
              <Space align="start">
                <QuestionCircleOutlined style={{ fontSize: 28, color: "#faad14" }} />
                <div>
                  <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
                    Preguntas frecuentes
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Respuestas rápidas a las consultas más habituales del puesto.
                  </Text>
                </div>
              </Space>
            </Card>
          </Link>
        </Col>
      </Row>
    </>
  );
};
